import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  BarChart3, TrendingUp, ChevronRight, ArrowLeft, Loader, AlertTriangle,
  Star, Shield, Target, Route, Users, CheckCircle, Info, ExternalLink
} from 'lucide-react'
import type { Report, DimensionScore, ScoreResult } from '../engine/types'
import { listReports, getReport } from '../engine/ReportEngine'
import { useLang } from '../i18n/LangContext'

const dimIcons: Record<string, React.ReactNode> = {
  '信息搜集': <Target size={14} />,
  'Information Gathering': <Target size={14} />,
  '预算管理': <BarChart3 size={14} />,
  'Budget Management': <BarChart3 size={14} />,
  '风险意识': <Shield size={14} />,
  'Risk Awareness': <Shield size={14} />,
  '路线规划': <Route size={14} />,
  'Route Planning': <Route size={14} />,
  '客户匹配': <Users size={14} />,
  'Customer Fit': <Users size={14} />,
}

function getLevelColor(level: string): string {
  switch (level) {
    case '优秀': return '#4ade80'
    case '良好': return '#60a5fa'
    case '基本': return '#fbbf24'
    case '不足': return '#f87171'
    default: return '#8b8fa8'
  }
}

function SimpleRadarChart({ data, labels, maxValue }: { data: number[]; labels: string[]; maxValue: number }) {
  const size = 200
  const center = size / 2
  const radius = 80
  const levels = 5
  const angleStep = (2 * Math.PI) / labels.length

  function getPoint(index: number, value: number) {
    const angle = angleStep * index - Math.PI / 2
    const r = (value / maxValue) * radius
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    }
  }

  const dataPoints = data.map((v, i) => getPoint(i, v))
  const polygonPoints = dataPoints.map(p => `${p.x},${p.y}`).join(' ')

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {Array.from({ length: levels }, (_, i) => {
        const r = (radius * (i + 1)) / levels
        const pts = Array.from({ length: labels.length }, (_, j) => {
          const angle = angleStep * j - Math.PI / 2
          return `${center + r * Math.cos(angle)},${center + r * Math.sin(angle)}`
        }).join(' ')
        return <polygon key={i} points={pts} fill="none" stroke="var(--color-border)" strokeWidth="0.5" />
      })}
      {labels.map((_, i) => {
        const p = getPoint(i, maxValue)
        return <line key={i} x1={center} y1={center} x2={p.x} y2={p.y} stroke="var(--color-border)" strokeWidth="0.5" />
      })}
      <polygon points={polygonPoints} fill="rgba(108,140,255,0.2)" stroke="var(--color-accent)" strokeWidth="1.5" />
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill="var(--color-accent)" />
      ))}
      {labels.map((label, i) => {
        const p = getPoint(i, maxValue * 1.25)
        return (
          <text
            key={i}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fill="var(--color-text-secondary)"
          >
            {label.length > 4 ? label.slice(0, 4) + '…' : label}
          </text>
        )
      })}
    </svg>
  )
}

export default function ReportPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useLang()
  const [reports, setReports] = useState<Report[]>([])
  const [selectedReport, setSelectedReport] = useState<Report | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      loadReportDetail(id)
    } else {
      loadReportList()
    }
  }, [id])

  async function loadReportList() {
    setLoading(true)
    setError(null)
    try {
      const data = await listReports()
      setReports(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load reports')
    } finally {
      setLoading(false)
    }
  }

  async function loadReportDetail(reportId: string) {
    setLoading(true)
    setError(null)
    try {
      const data = await getReport(reportId)
      setSelectedReport(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load report')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="container-page px-4 py-12">
        <div className="flex items-center justify-center py-24">
          <Loader className="animate-spin" size={32} style={{ color: 'var(--color-accent)' }} />
          <span className="ml-3 text-lg" style={{ color: 'var(--color-text-secondary)' }}>{t('common.loading')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container-page px-4 py-12">
        <div className="flex flex-col items-center justify-center py-24">
          <AlertTriangle size={48} style={{ color: 'var(--color-danger)' }} />
          <p className="mt-4 text-lg" style={{ color: 'var(--color-danger)' }}>{error}</p>
          <button onClick={() => id ? loadReportDetail(id) : loadReportList()} className="mt-4 px-4 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}>{t('common.retry')}</button>
        </div>
      </div>
    )
  }

  if (selectedReport) {
    const dims = selectedReport.dimensions || {}
    const dimEntries = Object.entries(dims)
    const radarData = dimEntries.map(([, v]) => v.score)
    const radarLabels = dimEntries.map(([k]) => k.length > 4 ? k.slice(0, 4) + '…' : k)
    const radarMax = dimEntries.map(([, v]) => v.max)
    const strengths = selectedReport.strengths || []
    const weaknesses = selectedReport.weaknesses || []

    return (
      <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => { setSelectedReport(null); navigate('/reports') }} className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
            <ArrowLeft size={18} style={{ color: 'var(--color-text-secondary)' }} />
          </button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('report.competencyReport')}</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              {selectedReport.agent_name} · {selectedReport.competency_name_cn || selectedReport.competency_name}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => navigate(`/traces/${selectedReport.trace_id}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors"
              style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
            >
              <ExternalLink size={14} /> {t('report.viewTrace')}
            </button>
          </div>
        </div>

        {/* Score Hero */}
        <div className="rounded-xl p-6 mb-6 text-center"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            background: 'linear-gradient(135deg, rgba(108,140,255,0.1) 0%, rgba(108,140,255,0.02) 100%)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{t('report.totalScore')}</p>
          <p className="text-5xl font-bold mt-1" style={{ color: 'var(--color-accent)' }}>{selectedReport.total_score.toFixed(1)}</p>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>Agent: {selectedReport.agent_name}</p>
        </div>

        {/* Radar Chart + Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="rounded-xl p-6 flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
            <SimpleRadarChart data={radarData} labels={radarLabels} maxValue={Math.max(...radarMax, 1)} />
          </div>

          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>{t('report.breakdown')}</h3>
            <div className="space-y-3">
              {dimEntries.map(([dimName, dimScore]: [string, DimensionScore]) => {
                const pct = dimScore.max > 0 ? (dimScore.score / dimScore.max) * 100 : 0
                const levelColor = getLevelColor(dimScore.level)
                return (
                  <div key={dimName}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        {dimIcons[dimName] || <Star size={14} />}
                        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{dimName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${levelColor}20`, color: levelColor }}>{dimScore.level}</span>
                        <span className="text-sm font-mono" style={{ color: 'var(--color-text-secondary)' }}>{dimScore.score}/{dimScore.max}</span>
                      </div>
                    </div>
                    <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: levelColor }} />
                    </div>
                    {dimScore.evidence && dimScore.evidence.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {dimScore.evidence.map((e, i) => (
                          <span key={i} className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>{e}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Strengths & Weaknesses */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-success)' }}>
              <TrendingUp size={18} /> {t('report.strengths')}
            </h3>
            {strengths.length > 0 ? (
              <div className="space-y-2">
                {strengths.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg" style={{ backgroundColor: 'rgba(74,222,128,0.08)' }}>
                    <CheckCircle size={14} style={{ color: 'var(--color-success)', marginTop: '2px' }} />
                    <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{s}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No strengths identified</p>
            )}
          </div>

          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-warning)' }}>
              <AlertTriangle size={18} /> {t('report.weaknesses')}
            </h3>
            {weaknesses.length > 0 ? (
              <div className="space-y-2">
                {weaknesses.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg" style={{ backgroundColor: 'rgba(251,191,36,0.08)' }}>
                    <Info size={14} style={{ color: 'var(--color-warning)', marginTop: '2px' }} />
                    <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{w}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No weaknesses identified</p>
            )}
          </div>
        </div>

        {/* Summary */}
        {selectedReport.summary && (
          <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>{t('report.summary')}</h3>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>{selectedReport.summary}</p>
          </div>
        )}
      </div>
    )
  }

  // Report list view
  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('report.title')}</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{t('report.subtitle')}</p>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
        {reports.length === 0 ? (
          <div className="text-center py-16">
            <BarChart3 size={40} style={{ color: 'var(--color-text-muted)' }} />
            <p className="mt-3 text-lg" style={{ color: 'var(--color-text-muted)' }}>{t('report.noReports')}</p>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>{t('report.runToGenerate')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Report ID</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Agent</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Competency</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Total Score</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Created</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}></th>
                </tr>
              </thead>
              <tbody>
                {reports.map(report => (
                  <tr
                    key={report.id}
                    className="cursor-pointer transition-colors hover:opacity-80"
                    style={{ borderBottom: '1px solid var(--color-border)' }}
                    onClick={() => navigate(`/reports/${report.id}`)}
                  >
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono" style={{ color: 'var(--color-accent)' }}>{report.id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{report.agent_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{report.competency_name_cn || report.competency_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold" style={{ color: report.total_score >= 80 ? 'var(--color-success)' : report.total_score >= 60 ? 'var(--color-warning)' : 'var(--color-danger)' }}>
                        {report.total_score.toFixed(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{new Date(report.created_at).toLocaleDateString('zh-CN')}</span>
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight size={16} style={{ color: 'var(--color-text-muted)' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}